import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import { ProvisioningGuard } from "@/components/auth/ProvisioningGuard";
import { AppAccessGuard } from "@/components/auth/AppAccessGuard";
import { AppInstallGuard } from "@/components/auth/AppInstallGuard";
import { CrmDashboardPage } from "@/features/crm";
import { TwentyCrmDashboardPage } from "@/features/twenty-crm";
import { InsightsDashboardPage } from "@/features/insights";
import { RavenDashboardPage } from "@/features/raven";
import { InboxPage } from "@/features/inbox";
import { InboxNotInstalledFallback } from "@/features/inbox/InboxNotInstalledFallback";
import { BlogDashboardPage } from "@/features/blog/BlogDashboardPage";
import { AiPage } from "@/features/ai/AiPage";
import { SheetsPage } from "@/features/sheets/SheetsPage";
import { AffineDashboardPage } from "@/features/affine/AffineDashboardPage";
import { ComingSoonPage } from "@/features/common/ComingSoonPage";
import { AppMarketplacePage } from "@/features/apps/AppMarketplacePage";
import { MobileDesignPage } from "@/features/storefront/mobile-design";

const BuildPackagePage = lazy(() => import("@/features/build/BuildPackagePage"));
const AppDetailPage = lazy(() => import("@/features/apps/AppDetailPage"));

/**
 * Non-ERP routes rendered directly under MainLayout (no ErpLayout sidebar).
 * Includes iframe-embedded apps (CRM, Raven, Insights, Inbox, Affine),
 * full-bleed editors, and utility pages.
 * Extracted from routes/index.tsx for file size management.
 */
export const iframeAppRoutes: RouteObject[] = [
  // Mobile Studio Builder — full-bleed, no sidebar, requires install + builder access
  {
    path: "storefront/mobile-design",
    element: <AppInstallGuard requiredApp="frappe_builder"><AppAccessGuard requiredApp="builder"><MobileDesignPage /></AppAccessGuard></AppInstallGuard>,
  },

  // Pick, Sort, Pack, Complete moved OUTSIDE MainLayout (see top-level fulfillment routes)

  // Twenty CRM Dashboard - provisioning guard only (no Available App record yet)
  {
    path: "twenty-crm/*",
    element: (
      <ProvisioningGuard>
        <TwentyCrmDashboardPage />
      </ProvisioningGuard>
    ),
  },
  // CRM Dashboard (Frappe CRM) - require install + app access + provisioning
  {
    path: "crm/*",
    element: (
      <AppInstallGuard requiredApp="crm">
        <AppAccessGuard requiredApp="crm">
          <ProvisioningGuard>
            <CrmDashboardPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  // Insights Dashboard - require install + app access + provisioning
  {
    path: "insights/*",
    element: (
      <AppInstallGuard requiredApp="insights">
        <AppAccessGuard requiredApp="insights">
          <ProvisioningGuard>
            <InsightsDashboardPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  // Raven (Team Chat) - require install + app access + provisioning
  {
    path: "raven/*",
    element: (
      <AppInstallGuard requiredApp="raven">
        <AppAccessGuard requiredApp="raven">
          <ProvisioningGuard>
            <RavenDashboardPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  // Inbox (Chatwoot) - require install + app access + provisioning
  {
    path: "inbox/*",
    element: (
      <AppInstallGuard requiredApp="chatwoot" fallback={<InboxNotInstalledFallback />}>
        <AppAccessGuard requiredApp="inbox">
          <ProvisioningGuard>
            <InboxPage />
          </ProvisioningGuard>
        </AppAccessGuard>
      </AppInstallGuard>
    ),
  },
  // Blog (Ghost) - require install + provisioning
  {
    path: "blog/*",
    element: (
      <AppInstallGuard requiredApp="ghost">
        <ProvisioningGuard>
          <BlogDashboardPage />
        </ProvisioningGuard>
      </AppInstallGuard>
    ),
  },
  // AI Assistant (LobeChat) - require provisioning only
  {
    path: "ai",
    element: (
      <ProvisioningGuard>
        <AiPage />
      </ProvisioningGuard>
    ),
  },
  // Sheets (Univer Workspace, Plan 1966) - require provisioning. Tenant enablement is
  // decided by CP (univer_integration_status); PersistentUniver shows a calm
  // "not turned on yet" state when CP answers UNIVER_NOT_ENABLED.
  {
    path: "sheets/*",
    element: (
      <ProvisioningGuard>
        <SheetsPage />
      </ProvisioningGuard>
    ),
  },
  // Mail - require install
  {
    path: "mail",
    element: (
      <AppInstallGuard requiredApp="mail">
        <ComingSoonPage />
      </AppInstallGuard>
    ),
  },
  // Affine (Notes) - require install + provisioning
  {
    path: "affine",
    element: (
      <AppInstallGuard requiredApp="affine">
        <ProvisioningGuard>
          <AffineDashboardPage />
        </ProvisioningGuard>
      </AppInstallGuard>
    ),
  },
  // Build Your Package — static pricing configurator (no provisioning required)
  {
    path: "build",
    element: (
      <Suspense fallback={null}>
        <BuildPackagePage />
      </Suspense>
    ),
  },
  {
    path: "apps",
    element: <AppMarketplacePage />,
  },
  {
    path: "apps/:appName",
    element: <Suspense fallback={null}><AppDetailPage /></Suspense>,
  },
  {
    path: "all-apps",
    element: <ComingSoonPage />,
  },
  // Catch-all route for 404 (outside ErpLayout — no sidebar on 404 pages)
  {
    path: "*",
    element: <ComingSoonPage />,
  },
];
